export const codeSnippet = {
	code1: `| Version        | Release Year | Key Highlights                                                          |
| -------------- | ------------ | ----------------------------------------------------------------------- |
| Angular 2  | 2016         | Complete rewrite of AngularJS, TypeScript-based                         |
| Angular 4  | 2017         | Smaller bundles, faster rendering (v3 skipped)                          |
| Angular 5  | 2017         | Build optimizer, improved compiler                                      |
| Angular 6  | 2018         | Angular CLI, RxJS 6, ng update                                          |
| Angular 7  | 2018         | Performance improvements, virtual scrolling                             |
| Angular 8  | 2019         | Differential loading, lazy loading improvements                         |
| Angular 9  | 2020         | Ivy renderer (major change)                                             |
| Angular 10 | 2020         | Strict typing, ecosystem updates                                        |
| Angular 11 | 2020         | Faster builds, improved HMR                                             |
| Angular 12 | 2021         | Ivy default, stricter typing                                            |
| Angular 13 | 2021         | Removed View Engine                                                     |
| Angular 14 | 2022         | Typed forms, standalone components (preview)                            |
| Angular 15 | 2022         | Standalone components stable                                            |
| Angular 16 | 2023         | Signals (reactivity), better hydration                                  |
| Angular 17 | 2023         | New control flow ('@if', '@for'), faster builds                         |
| Angular 18 | 2024         | Improved signals, SSR enhancements                                      |
| Angular 19 | 2024         | Performance + developer experience upgrades                             |
| Angular 20 | 2025         | Advanced hydration, AI tooling integration (expected/ongoing evolution) |
`,
  code2: `// user.component.ts
    @Component({
        selector: 'app-user',
        template: '<div>{{user.name}}</div>'
        })
        export class UserComponent {
        user = { name: 'John' };
    }

    // app.module.ts
    @NgModule({
    declarations: [
        AppComponent,
        UserComponent,  // Must declare here
        HeaderComponent
    ],
    imports: [
        BrowserModule,
        FormsModule,
        HttpClientModule
    ],
    providers: [UserService],
    bootstrap: [AppComponent]
    })
    export class AppModule { }`,
  code3: `    // user.component.ts
    @Component({
        selector: 'app-user',
        standalone: true,           // 👈 Key difference
        imports: [CommonModule],    // 👈 Import dependencies directly
        template: '<div>{{user.name}}</div>'
    })
    export class UserComponent {
        user = { name: 'John' };
    }

    // app.component.ts
    @Component({
        selector: 'app-root',
        standalone: true,
        imports: [UserComponent],   // 👈 Import component directly
        template: '<app-user></app-user>'
    })
    export class AppComponent { }
`,
  code4: `ng new my-app --standalone

ng generate component user-list
ng g c user-detail

ng generate service user
ng g s auth

ng generate guard auth
ng g g can-activate

ng generate pipe capitalize
ng g p currency`,
  code5: `my-app/
├── node_modules/
├── src/
  ├── app/
      ├── components/
      ├── services/
      ├── models/
      ├── pages/
      ├── app.component.ts
      ├── app.routes.ts  
  ├── assets/
  ├── environments/
  ├── index.html
  ├── main.ts
  ├── styles.css
├── angular.json
├── package.json
├── tsconfig.json`,
  code6: `import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';

bootstrapApplication(AppComponent);`,
  code7: `@Component({
  selector: 'app-root',
  standalone: true,
  template: '<h1>Hello Angular</h1>'
})
export class AppComponent {}`,
  code8: `import { Routes } from '@angular/router';
import { HomeComponent } from './pages/home.component';

export const routes: Routes = [
  { path: '', component: HomeComponent }
];`,
  code9: `import { Component } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UserService } from './services/user.service';

@Component({
  selector: 'app-user',
  standalone: true,
  imports: [CommonModule],
  template: '<div>{{user.name}}</div>'
})
export class UserComponent {
  user: any = {};

  constructor(
    private http: HttpClient,
    private userService: UserService
  ) {}

  ngOnInit() {
      this.userService.getUser(1).subscribe(user => {
      this.user = user;
      });
  }
}
`,
  code10: `import { Component, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UserService } from './services/user.service';

@Component({
selector: 'app-user',
standalone: true,
template: '<div>{{user.name}}</div>'
})
export class UserComponent {
  private http = inject(HttpClient);
  private userService = inject(UserService);
  
  user: any = {};

  ngOnInit() {
    this.userService.getUser(1).subscribe(user => {
      this.user = user;
    });
  }
}


export const appConfig: ApplicationConfig = {
  providers: [
    { provide: 'API_URL', useValue: 'https://api.example.com' },
    { provide: 'APP_CONFIG', useValue: { version: '1.0.0', debug: true } }
  ]
};

// component usage
@Component({
  selector: 'app-example',
  standalone: true
})
export class ExampleComponent {
  private apiUrl = inject('API_URL');
  private config = inject('APP_CONFIG');

  // Or with constructor
  constructor(
    @Inject('API_URL') private apiUrl: string,
    @Inject('APP_CONFIG') private config: any
  ) {}
}
`,
  code11: ` // Main.ts
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';

bootstrapApplication(AppComponent, appConfig);
//                               ↑ pass config here`,
  code12: `import { ApplicationConfig } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient } from '@angular/common/http';
import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideHttpClient(),
  ]
};`,
  code13: `| Provider Type | What it Does           | Use Case            |
| ------------- | ---------------------- | ------------------- |
| 'useClass'    | Creates class instance | Services            |
| 'useValue'    | Static value           | Config/constants    |
| 'useFactory'  | Dynamic value          | Runtime logic       |
| 'useExisting' | Alias                  | Reuse same instance |
`,
  code14: `import { InjectionToken } from '@angular/core';

export const API_URL = new InjectionToken<string>('API_URL');
export const APP_CONFIG = new InjectionToken<any>('APP_CONFIG');
export const LOGGER = new InjectionToken<any>('LOGGER');`,
  code15: `export class LoggerService {
  log(message: string) {
    console.log('Logger:', message);
  }
}`,
  code16: `export class BetterLoggerService {
  log(message: string) {
    console.log('🔥 BetterLogger:', message);
  }
}`,
  code17: `import { ApplicationConfig } from '@angular/core';
import { API_URL, APP_CONFIG, LOGGER } from './tokens';
import { LoggerService } from './logger.service';
import { BetterLoggerService } from './better-logger.service';

export const appConfig: ApplicationConfig = {
  providers: [

    // ✅ 1. useValue (static config)
    {
      provide: APP_CONFIG,
      useValue: { version: '1.0.0', debug: true }
    },

    // ✅ 2. useFactory (dynamic API URL)
    {
      provide: API_URL,
      useFactory: () => {
        return window.location.hostname === 'localhost'
          ? 'http://localhost:3000'
          : 'https://api.production.com';
      }
    },

    // ✅ 3. useClass (replace service implementation)
    {
      provide: LoggerService,
      useClass: BetterLoggerService
    },

    // ✅ 4. useExisting (alias)
    {
      provide: LOGGER,
      useExisting: LoggerService
    }
  ]
};`,
  code18: `import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';

bootstrapApplication(AppComponent, appConfig);`,
  code19: `import { Component, inject } from '@angular/core';
import { API_URL, APP_CONFIG, LOGGER } from './tokens';
import { LoggerService } from './logger.service';

@Component({
  selector: 'app-root',
  standalone: true,
  template: '
    <h2>Check Console</h2>
    <p>API: {{ apiUrl }}</p>
    <p>Version: {{ config.version }}</p>
  '
})
export class AppComponent {

  apiUrl = inject(API_URL);          // from useFactory
  config = inject(APP_CONFIG);       // from useValue
  logger = inject(LoggerService);    // from useClass
  aliasLogger = inject(LOGGER);      // from useExisting

  constructor() {
    this.logger.log('Using LoggerService');
    this.aliasLogger.log('Using Alias Logger');
  }
}`,
  code20: `import { Component, Inject } from '@angular/core';
import { API_URL } from './tokens';

@Component({
  selector: 'app-root',
  template: '{{ apiUrl }}'
})
export class AppComponent {
  constructor(@Inject(API_URL) public apiUrl: string) {}
}`,
  code21: `import { Component, signal } from '@angular/core';

@Component({
  selector: 'app-counter',
  standalone: true,
  template: '
    <h1>Count: {{ count() }}</h1> <!-- Read signal value with count() -->
    <button (click)="increment()">Increase</button>
  '
})
export class CounterComponent {
  count = signal(0);

  increment() {
    this.count.update(v => v + 1);
    this.count.set(5); // Directly set value
  }
}`,
  code22: `import { Component, signal, computed, effect } from '@angular/core';
  @Component({
      selector: 'app-counter',
      standalone: true,
      template: '
          <div>
          <h2>Count: {{count()}}</h2>
          <h3>Double: {{doubleCount()}}</h3>
          <button (click)="increment()">+</button>
          <button (click)="decrement()">-</button>
          <button (click)="reset()">Reset</button>
          </div>'
  })
  export class CounterComponent {
    // Creating a writable signal
    count = signal(0);
    
    // Creating a computed signal (read-only)
    doubleCount = computed(() => this.count() * 2);
    
    initials = computed(() => {
      const first = this.firstName();
      const last = this.lastName();
      return (first.charAt(0) + last.charAt(0)).toUpperCase();
    });

    constructor() {
      // Effect - runs when signals change
      effect(() => {
        console.log('Count changed to:', this.count());
      });
    }

    // Methods to update the signal
    increment() {
      // Update based on current value
      this.count.update(value => value + 1);
    }

    decrement() {
      // Update based on current value
      this.count.update(value => value - 1);
    }

    reset() {
      // Set new value
      this.count.set(0);
    }
  }

  interface User {
    id: number;
    name: string;
    preferences: {
      theme: string;
      notifications: boolean;
    };
  }

  const user = signal<User>({
    id: 1,
    name: 'John',
    preferences: {
      theme: 'dark',
      notifications: true
    }
  });

  // Update entire object
  user.set({
    id: 1,
    name: 'John Doe',
    preferences: {
      theme: 'light',
      notifications: false
    }
  });

  // Update specific property
  user.update(current => ({
    ...current,
    name: 'Jane Doe'
  }));

  // Update nested property
  user.update(current => ({
    ...current,
    preferences: {
      ...current.preferences,
      theme: 'dark'
    }
  }));`,
  code23: `<!-- New @if syntax -->
@if (user.isLoggedIn) {
  <div>Welcome {{user.name}}!</div>
} @else {
  <div>Please log in</div>
}

<!-- New @for syntax with track -->
@for (item of items; track item.id) {
  <div>{{item.name}}</div>
} @empty {
  <div>No items found</div>
}

<!-- New @switch syntax -->
@switch (status) {
  @case ('loading') {
    <div>Loading...</div>
  }
  @case ('error') {
    <div>Error occurred</div>
  }
  @default {
    <div>Content loaded</div>
  }
} `,
  code24: `@Component({...})
export class UserComponent {
  @Input({ required: true }) userId!: string;  // Required input
  @Input() name?: string;  // Optional input
} `,
  code25: `<div [textContent]="user().name"></div>`,
  code26: `@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  constructor(private auth: AuthService) {}

  canActivate(): boolean {
    return this.auth.isLoggedIn();
  }
}`,
  code27: `import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { AuthService } from './auth.service';

export const authGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  return auth.isLoggedIn();
};`,
  code28: `import { Routes } from '@angular/router';
import { authGuard } from './auth.guard';

export const routes: Routes = [
  {
    path: 'dashboard',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./dashboard.component').then(m => m.DashboardComponent)
  },
  {
    path: 'admin',
    loadChildren: () => import('./admin.routes'),
    canActivate: [authGuard, adminGuard]  // Multiple guards
  },
  {
    path: 'form',
    loadComponent: () => import('./form.component'),
    canDeactivate: [formDeactivateGuard]
  },
  {
    path: 'user/:id',
    loadComponent: () => import('./user.component'),
    resolve: { user: userResolver }  // Pre-load data
  }
];`,
  code29: `import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthService } from './auth.service';

export const authGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (auth.isLoggedIn()) {
    return true;
  }

  return router.createUrlTree(['/login']);
};`,
  code30: `import { CanDeactivateFn } from '@angular/router';

export const confirmExitGuard: CanDeactivateFn<any> = (component) => {
  return confirm('Are you sure you want to leave?');
};`,
  code31: `| Guard Type           | Purpose                       |
| -------------------- | ----------------------------- |
| 'CanActivateFn'      | Allow/deny route              |
| 'CanActivateChildFn' | Protect child routes          |
| 'CanDeactivateFn'    | Prevent leaving page          |
| 'ResolveFn'          | Fetch data before route loads |
| 'CanMatchFn'         | Decide if route should match  |
`,
  code32: `// http.interceptor.ts
import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

export const httpInterceptor: HttpInterceptorFn = (req, next) => {
console.log('Request URL:', req.url);

  // Modify request
  const modifiedReq = req.clone({
      setHeaders: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
      }
  });

  return next(modifiedReq);
};


// auth.interceptor.ts
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  
  // Skip auth for public endpoints
  const publicEndpoints = ['/auth/login', '/auth/register', '/auth/refresh'];
  const isPublicEndpoint = publicEndpoints.some(endpoint => req.url.includes(endpoint));
  
  if (isPublicEndpoint) {
    return next(req);
  }
  
  // Add authorization header
  const token = authService.getToken();
  const authReq = token ? req.clone({
    setHeaders: {
      Authorization: 'Bearer token'
    }
  }) : req;
    
  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
    if (error.status === 401) {
        authService.logout();
        router.navigate(['/login']);
    }
    return throwError(() => error);
  })
  );
};

// JWT Token Refresh Interceptor
export const tokenRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
    if (error.status === 401 && !req.url.includes('/auth/refresh')) {
      // Attempt token refresh
      return authService.refreshToken().pipe(
      switchMap((newToken) => {
        // Retry original request with new token
        const retryReq = req.clone({
        setHeaders: {
          Authorization: 'Bearer newToken'
        }
      });
      return next(retryReq);
    }),
    catchError((refreshError) => {
      authService.logout();
      return throwError(() => refreshError);
    })
      );
    }
    return throwError(() => error);
    })
  );
};

// main.ts
import { bootstrapApplication } from '@angular/platform-browser';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { AppComponent } from './app/app.component';
import { 
authInterceptor,
errorInterceptor,
loadingInterceptor,
loggingInterceptor,
cacheInterceptor 
} from './interceptors';

bootstrapApplication(AppComponent, {
providers: [
  provideHttpClient(
  withInterceptors([
    loggingInterceptor,      // First: Log all requests
    authInterceptor,         // Second: Add authentication
    loadingInterceptor,      // Third: Show loading
    cacheInterceptor,        // Fourth: Check cache
    errorInterceptor         // Last: Handle errors
  ])
  ),
  // Other providers...
]
});`,
  code33: `| API               | When it runs                    |
| ----------------- | ------------------------------- |
| 'afterNextRender' | Runs once after next render     |
| 'afterRender'     | Runs after every render         |`,
  code34: `import { Component, afterNextRender } from '@angular/core';
@Component({
  selector: 'app-demo',
  standalone: true,
  template: '<h1>Hello</h1>'
})
export class DemoComponent {
  constructor() {
    afterNextRender(() => {
      console.log('DOM rendered once');
    });
  }
}`,
  code35: `import { Component, afterRender, signal } from '@angular/core';

@Component({
  selector: 'app-demo',
  standalone: true,
  template: '<h1>{{ count() }}</h1>'
})
export class DemoComponent {

  count = signal(0);

  constructor() {
    afterRender(() => {
      console.log('DOM updated');
    });
  }

  increment() {
    this.count.update(v => v + 1);
  }
}`,
  code36: `| Old Hook             | New Alternative   |
| -------------------- | ----------------- |
| 'ngAfterViewInit'    | 'afterNextRender' |
| 'ngAfterViewChecked' | 'afterRender'    |`,
  code37: ``,
  code38: ``,
  code39: ``,
  code40: ``,
  code41: ``,
  code42: ``,
  code43: ``,
  code44: ``,
  code45: ``,
  code46: ``,
  code47: ``,
  code48: ``,
  code49: ``,
}